import { Link } from "react-router-dom";
import { Minus, Plus, ShoppingCart, Trash2, QrCode } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatBRL, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export const CartView = ({ testPrefix = "cart" }) => {
    const { items, setQuantity, remove, totalCents, clear } = useCart();
    const { user, login } = useAuth();

    const checkout = async () => {
        if (items.length === 0) return;
        if (!user) {
            localStorage.setItem("zf_pending_checkout", "1");
            login();
            return;
        }
        try {
            const { data } = await api.post("/checkout", {
                items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
            });
            toast.success("Pedido criado! Redirecionando para o pagamento...");
            clear();
            window.location.href = data.checkout_url;
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.setItem("zf_pending_checkout", "1");
                login();
            } else {
                toast.error(formatApiErrorDetail(err.response?.data?.detail));
            }
        }
    };

    if (items.length === 0) {
        return (
            <div
                className="layer-lines flex min-h-[280px] flex-col items-center justify-center gap-4 border border-dashed border-forge-line bg-forge-panel/40 p-10 text-center"
                data-testid={`${testPrefix}-empty-state`}
            >
                <ShoppingCart className="h-10 w-10 text-forge-yellow/60" />
                <p className="font-display text-lg font-bold uppercase text-forge-paper">Carrinho vazio</p>
                <p className="max-w-xs text-sm leading-relaxed text-forge-dim">
                    Suas peças impressas ficam guardadas aqui até o checkout.
                </p>
                <Link
                    to="/catalogo"
                    data-testid={`${testPrefix}-empty-catalog-button`}
                    className="mt-2 bg-forge-yellow px-8 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-forge-ink transition-transform duration-300 hover:-translate-y-0.5"
                >
                    Explorar catálogo
                </Link>
            </div>
        );
    }

    return (
        <div>
            <div className="space-y-4" data-testid={`${testPrefix}-items-list`}>
                {items.map((i) => (
                    <div
                        key={i.product_id}
                        data-testid={`${testPrefix}-item-${i.product_id}`}
                        className="flex items-center gap-4 border border-forge-line bg-forge-panel p-4"
                    >
                        <img
                            src={fileUrl(i.image)}
                            alt={i.name}
                            className="h-20 w-20 shrink-0 border border-forge-line object-cover"
                        />
                        <div className="min-w-0 flex-1">
                            <p className="truncate font-display text-sm font-bold uppercase text-forge-paper">{i.name}</p>
                            <p className="mt-1 font-mono text-xs text-forge-yellow">{formatBRL(i.price_cents)}</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setQuantity(i.product_id, i.quantity - 1)}
                                data-testid={`${testPrefix}-qty-minus-${i.product_id}`}
                                aria-label="Diminuir quantidade"
                                className="flex h-8 w-8 items-center justify-center border border-forge-line text-forge-paper transition-colors duration-300 hover:border-forge-yellow hover:text-forge-yellow"
                            >
                                <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span className="w-6 text-center font-mono text-sm text-forge-paper" data-testid={`${testPrefix}-qty-${i.product_id}`}>
                                {i.quantity}
                            </span>
                            <button
                                onClick={() => setQuantity(i.product_id, i.quantity + 1)}
                                data-testid={`${testPrefix}-qty-plus-${i.product_id}`}
                                aria-label="Aumentar quantidade"
                                className="flex h-8 w-8 items-center justify-center border border-forge-line text-forge-paper transition-colors duration-300 hover:border-forge-yellow hover:text-forge-yellow"
                            >
                                <Plus className="h-3.5 w-3.5" />
                            </button>
                        </div>
                        <button
                            onClick={() => remove(i.product_id)}
                            data-testid={`${testPrefix}-remove-${i.product_id}`}
                            aria-label={`Remover ${i.name}`}
                            className="flex h-9 w-9 items-center justify-center border border-forge-line text-forge-dim transition-colors duration-300 hover:border-red-500 hover:text-red-400"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-6 border border-forge-line bg-forge-panel p-6 md:p-8">
                <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-forge-dim">Total</p>
                    <p className="mt-1 font-display text-2xl font-black text-forge-yellow" data-testid={`${testPrefix}-total`}>
                        {formatBRL(totalCents)}
                    </p>
                </div>
                <div className="text-right">
                    <button
                        onClick={checkout}
                        data-testid={`${testPrefix}-checkout-button`}
                        className="inline-flex items-center gap-3 bg-forge-yellow px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-forge-ink transition-transform duration-300 hover:-translate-y-0.5 md:text-sm"
                    >
                        <QrCode className="h-4 w-4" />
                        Pagar com PIX
                    </button>
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-forge-dim">
                        {user ? "Você escaneará o QR code do PIX — pagamento seguro via Stripe" : "Você entrará na sua conta antes de pagar"}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CartView;
